"use client";

import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Zap, BarChart3, HeartHandshake, Rocket } from "lucide-react";
import { useLocale } from "next-intl";

const Mission = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const locale = useLocale();

  const missionPoints = [
    {
      icon: Zap,
      text:
        locale === "en"
          ? "Reduce operational errors"
          : "تقليل الأخطاء التشغيلية",
    },
    {
      icon: BarChart3,
      text: locale === "en" ? "Improve decision-making" : "تحسين اتخاذ القرار",
    },
    {
      icon: HeartHandshake,
      text:
        locale === "en" ? "Support sustainable growth" : "دعم النمو المستدام",
    },
  ];

  return (
    <section
      id="mission"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ backgroundColor: "#f7f9fc" }} // --color-background-light (white-ish)
    >
      {/* Subtle horizontal lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-linear-to-r from-transparent via-[#c9a24d]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-linear-to-r from-transparent via-[#c9a24d]/30 to-transparent" />

      {/* Very subtle gold radial glow */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-200 h-200 opacity-10"
        style={{
          background:
            "radial-gradient(ellipse at center, #c9a24d 0%, transparent 60%)",
        }}
      />

      <div ref={ref} className="container mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="order-2 lg:order-1"
          >
            <div className="relative w-full max-w-md mx-auto">
              <svg className="w-full h-auto" viewBox="0 0 400 300">
                {/* Connecting lines - gold & navy */}
                <motion.path
                  d="M200 50 L200 250"
                  stroke="#c9a24d"
                  strokeWidth="3"
                  strokeDasharray="10 5"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={isInView ? { pathLength: 1, opacity: 0.6 } : {}}
                  transition={{ duration: 1.8, delay: 0.3 }}
                />
                <motion.path
                  d="M100 150 L300 150"
                  stroke="#c9a24d"
                  strokeWidth="3"
                  strokeDasharray="10 5"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={isInView ? { pathLength: 1, opacity: 0.6 } : {}}
                  transition={{ duration: 1.8, delay: 0.5 }}
                />
                <motion.path
                  d="M120 80 L280 220"
                  stroke="#e6c77a"
                  strokeWidth="2"
                  strokeDasharray="8 4"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={isInView ? { pathLength: 1, opacity: 0.5 } : {}}
                  transition={{ duration: 1.8, delay: 0.7 }}
                />
                <motion.path
                  d="M280 80 L120 220"
                  stroke="#e6c77a"
                  strokeWidth="2"
                  strokeDasharray="8 4"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={isInView ? { pathLength: 1, opacity: 0.5 } : {}}
                  transition={{ duration: 1.8, delay: 0.9 }}
                />
              </svg>

              {/* Central rocket */}
              <motion.div
                initial={{ opacity: 0, scale: 0.6 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.7, delay: 0.4, type: "spring" }}
                viewport={{ once: true }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              >
                <div className="w-28 h-28 rounded-2xl bg-linear-to-br from-[#0a1b2e] to-[#132b44] flex items-center justify-center shadow-xl">
                  <Rocket size={48} className="text-[#c9a24d]" />
                </div>
              </motion.div>

              {/* Orbiting icons - gold */}
              {missionPoints.map((point, index) => {
                const angle = (index * 120 - 90) * (Math.PI / 180);
                const radius = 130;
                const x = 200 + radius * Math.cos(angle);
                const y = 150 + radius * Math.sin(angle);

                return (
                  <motion.div
                    key={point.text}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{
                      duration: 0.7,
                      delay: 0.6 + index * 0.2,
                      type: "spring",
                    }}
                    viewport={{ once: true }}
                    className="absolute p-4 rounded-xl bg-white/80 backdrop-blur-sm border border-[#c9a24d]/30 shadow-md"
                    style={{
                      left: `calc(${(x / 400) * 100}% - 32px)`,
                      top: `calc(${(y / 300) * 100}% - 32px)`,
                    }}
                  >
                    <point.icon size={32} className="text-[#c9a24d]" />
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="order-1 lg:order-2"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="inline-block text-[#c9a24d] font-semibold  mb-4  text-4xl md:text-6xl"
            >
              {locale === "en" ? "Our Mission" : "مهمتنا"}
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-xl font-extrabold text-[#0a1b2e] mb-8"
            >
              {locale === "en" ? "Driving " : "نقود "}
              <span className="bg-linear-to-r from-[#c9a24d] to-[#e6c77a] bg-clip-text text-transparent">
                {locale === "en" ? "Business Success" : "نجاح الأعمال"}
              </span>{" "}
              {locale === "en"
                ? "Through ERP Excellence"
                : "من خلال التميز في تخطيط موارد المؤسسات"}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-[#0f172a] text-lg lg:text-xl leading-relaxed mb-10 max-w-2xl"
            >
              {locale === "en"
                ? `  To help organizations successfully implement Odoo ERP by combining
              practical business understanding with structured implementation
              and ongoing technical support. We deliver systems that reduce
              operational errors, improve decision-making, and support
              sustainable growth.`
                : `لمساعدة المؤسسات على تنفيذ نظام Odoo ERP بنجاح من خلال الجمع بين
              الفهم العملي للأعمال والتنفيذ المنظم
              والدعم الفني المستمر. نحن نقدم أنظمة تقلل من
              الأخطاء التشغيلية، وتحسن اتخاذ القرار، وتدعم
              النمو المستدام.`}
            </motion.p>

            {/* Mission Points */}
            <div className="space-y-6">
              {missionPoints.map((point, index) => (
                <motion.div
                  key={point.text}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.15 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-5 group"
                >
                  <div className="w-14 h-14 rounded-xl bg-white border border-[#c9a24d]/40 flex items-center justify-center transition-all duration-500 group-hover:bg-[#c9a24d]/10 group-hover:border-[#c9a24d]/70 group-hover:scale-110">
                    <point.icon size={28} className="text-[#c9a24d]" />
                  </div>
                  <span className="text-[#0a1b2e] text-lg font-medium">
                    {point.text}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Mission;
